import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CalendarDaysIcon,
    BarsArrowDownIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    CheckCircleIcon,
    XCircleIcon,
    EyeIcon,
    ClockIcon,
    CheckIcon,
    MinusCircleIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import { Clock, BarChart3 } from 'lucide-react';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';
import { getPageConfig } from '../utils/pageConfigs';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement
);

// Mock data for leave requests
const mockLeaveRequests = [
    { id: 1, name: 'Jane Doe', type: 'Vacation', status: 'Pending', startDate: '2024-08-01', endDate: '2024-08-05', reason: 'Family trip' },
    { id: 2, name: 'John Smith', type: 'Sick Leave', status: 'Approved', startDate: '2024-07-25', endDate: '2024-07-25', reason: 'Flu' },
    { id: 3, name: 'Emily White', type: 'Personal', status: 'Pending', startDate: '2024-09-10', endDate: '2024-09-12', reason: 'Personal matters' },
    { id: 4, name: 'David Lee', type: 'Vacation', status: 'Approved', startDate: '2024-07-15', endDate: '2024-07-20', reason: 'Beach holiday' },
    { id: 5, name: 'Sarah Chen', type: 'Sick Leave', status: 'Rejected', startDate: '2024-07-22', endDate: '2024-07-22', reason: 'Fever' },
];

const mockSummary = {
    totalRequests: 5,
    pending: 2,
    approved: 2,
    rejected: 1,
};

const leaveTypeData = {
    labels: ['Vacation', 'Sick Leave', 'Personal'],
    datasets: [{
        data: [2, 2, 1],
        backgroundColor: ['#4C51BF', '#F97316', '#10B981'],
        borderColor: '#ffffff',
        borderWidth: 2,
    }],
};

const monthlyLeaveData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [{
        label: 'Total Leave Days',
        data: [15, 20, 10, 25, 18, 12, 30],
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: '#3B82F6',
        borderWidth: 1,
    }],
};

const LeaveManagement = () => {
    const [leaveRequests, setLeaveRequests] = useState(mockLeaveRequests);
    const [sortConfig, setSortConfig] = useState({ key: 'startDate', direction: 'ascending' });
    const [showModal, setShowModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // Get page-specific configurations
    const pageConfig = getPageConfig('Leave Management');

    const sortedRequests = useMemo(() => {
        let sortableRequests = [...leaveRequests];
        if (sortConfig.key) {
            sortableRequests.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableRequests;
    }, [leaveRequests, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <BarsArrowDownIcon className="w-4 h-4 ml-1 text-gray-400" />;
        return sortConfig.direction === 'ascending' ? <ArrowUpIcon className="w-4 h-4 ml-1" /> : <ArrowDownIcon className="w-4 h-4 ml-1" />;
    };

    const handleAction = (id, status) => {
        setLeaveRequests(leaveRequests.map(req => req.id === id ? { ...req, status } : req));
    };

    const handleView = (request) => {
        setSelectedRequest(request);
        setShowModal(true);
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-teal-100 to-lime-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Summary */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-green-400">
                    <h1 className="text-4xl font-extrabold text-green-800 tracking-tight mb-4 md:mb-0">
                        Leave Management
                    </h1>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                activeTab === 'dashboard' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Dashboard
                        </button>
                        <button
                            onClick={() => setActiveTab('activity')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                activeTab === 'activity' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            <Clock size={16} /> Activity Logs
                        </button>
                        <button
                            onClick={() => setActiveTab('reports')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                activeTab === 'reports' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            <BarChart3 size={16} /> Reports
                        </button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
                        >
                            <CalendarDaysIcon className="h-5 w-5" />
                            <span className="font-semibold">View Calendar</span>
                        </motion.button>
                    </div>
                </div>

                {activeTab === 'activity' && (
                    <ActivityLogs 
                        moduleName="Leave Management" 
                        activityTypes={pageConfig.activityTypes} 
                    />
                )}
                
                {activeTab === 'reports' && (
                    <Reports 
                        moduleName="Leave Management" 
                        reportTypes={pageConfig.reportTypes} 
                    />
                )}
                
                {activeTab === 'dashboard' && (
                    <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Total Requests</h3>
                        <p className="text-4xl font-extrabold text-indigo-600 mt-2">{mockSummary.totalRequests}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Pending</h3>
                        <p className="text-4xl font-extrabold text-yellow-600 mt-2">{mockSummary.pending}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Approved</h3>
                        <p className="text-4xl font-extrabold text-green-600 mt-2">{mockSummary.approved}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Rejected</h3>
                        <p className="text-4xl font-extrabold text-red-600 mt-2">{mockSummary.rejected}</p>
                    </motion.div>
                </div>

                {/* Charts & Leave Request Table */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Charts */}
                    <motion.div
                        className="lg:col-span-1 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-purple-600">
                            <ChartBarIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Leave Breakdown</h3>
                        </div>
                        <div className="h-64 flex items-center justify-center">
                            <Pie
                                data={leaveTypeData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { position: 'right' } }
                                }}
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-purple-600">
                            <ChartBarIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Monthly Leave Trends</h3>
                        </div>
                        <div className="h-64">
                            <Bar
                                data={monthlyLeaveData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: { y: { beginAtZero: true } }
                                }}
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Leave Requests Table */}
                <motion.div
                    className="mt-8 bg-white rounded-3xl shadow-xl overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['name', 'type', 'startDate', 'endDate', 'status', 'actions'].map(key => (
                                        <th
                                            key={key}
                                            onClick={() => key !== 'actions' && requestSort(key)}
                                            className={`px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider ${key !== 'actions' ? 'cursor-pointer hover:bg-gray-100 transition-colors' : 'text-right'}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                {key.replace(/([A-Z])/g, ' $1').charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').slice(1)}
                                                {key !== 'actions' && getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence initial={false}>
                                    {sortedRequests.length > 0 ? (
                                        sortedRequests.map(request => (
                                            <motion.tr
                                                key={request.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -20, transition: { duration: 0.3 } }}
                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{request.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{request.type}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{request.startDate}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{request.endDate}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        request.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                                        request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {request.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleView(request)} className="text-indigo-600 hover:text-indigo-900 transition-colors">
                                                            <EyeIcon className="h-5 w-5" />
                                                        </motion.button>
                                                        {request.status === 'Pending' && (
                                                            <>
                                                                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleAction(request.id, 'Approved')} className="text-green-600 hover:text-green-900 transition-colors">
                                                                    <CheckCircleIcon className="h-5 w-5" />
                                                                </motion.button>
                                                                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleAction(request.id, 'Rejected')} className="text-red-600 hover:text-red-900 transition-colors">
                                                                    <XCircleIcon className="h-5 w-5" />
                                                                </motion.button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No leave requests found.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
                    </>
                )}
            </motion.div>

            {/* View Leave Request Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            className="relative p-8 bg-white rounded-3xl shadow-xl w-full max-w-lg mx-auto"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 border-b pb-2 mb-4">Leave Request Details</h3>
                            {selectedRequest && (
                                <div className="space-y-4 text-sm text-gray-700">
                                    <p><span className="font-semibold">Name:</span> {selectedRequest.name}</p>
                                    <p><span className="font-semibold">Leave Type:</span> {selectedRequest.type}</p>
                                    <p><span className="font-semibold">Status:</span> {selectedRequest.status}</p>
                                    <p><span className="font-semibold">Dates:</span> {selectedRequest.startDate} to {selectedRequest.endDate}</p>
                                    <p><span className="font-semibold">Reason:</span> {selectedRequest.reason}</p>
                                </div>
                            )}
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-full shadow hover:bg-purple-700 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LeaveManagement;